import { useState } from "react";
import { STAGES, type StageDef } from "../lib/stages";
import type { BoardRow, Grupo, OnboardingPipeline } from "../lib/types";
import { resolveWhatsappGroup } from "../lib/api";
import { useToast } from "./Toast";
import styles from "./StageTimeline.module.css";

export interface MarcarPayload {
  data?: string; // ISO date escolhida (para etapas com extra "data")
  link?: string;
  grupo_id?: string;
  nota?: string;
}

interface Props {
  row: BoardRow;
  pipeline: OnboardingPipeline | null;
  stageExibicao: number;
  salvando: number | null; // n da etapa sendo salva
  sincronizando: boolean;
  onMarcar: (stage: StageDef, payload: MarcarPayload) => void;
  onSincronizar: () => void;
}

function fmt(ts: string | null | undefined): string | null {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function StageTimeline({ row, pipeline, stageExibicao, salvando, sincronizando, onMarcar, onSincronizar }: Props) {
  return (
    <ol className={styles.timeline}>
      {STAGES.map((s) => {
        const ts = row[s.campo];
        const concluida = !!ts;
        const data = fmt(ts);
        const atual = !concluida && s.n === stageExibicao + 1;
        return (
          <li key={s.n} className={`${styles.item} ${concluida ? styles.done : ""} ${atual ? styles.atual : ""}`}>
            <div className={styles.marker}>{concluida ? "✓" : s.n}</div>
            <div className={styles.body}>
              <div className={styles.titulo}>
                <span>{s.label}</span>
                {s.automatica && <span className="badge badge-neutral">automática</span>}
                {data && <span className={styles.data}>{data}</span>}
              </div>
              <p className={styles.desc}>{s.descricao}</p>

              {s.automatica && !concluida && (
                <button className="btn btn-ghost" onClick={onSincronizar} disabled={sincronizando}>
                  {sincronizando ? "Sincronizando…" : "Sincronizar status"}
                </button>
              )}

              {!s.automatica && !concluida && s.resolveGrupo && (
                <GrupoForm stage={s} salvando={salvando === s.n} onMarcar={onMarcar} />
              )}

              {!s.automatica && !concluida && !s.resolveGrupo && (
                <MarcarForm stage={s} pipeline={pipeline} salvando={salvando === s.n} onMarcar={onMarcar} />
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function MarcarForm({
  stage,
  pipeline,
  salvando,
  onMarcar,
}: {
  stage: StageDef;
  pipeline: OnboardingPipeline | null;
  salvando: boolean;
  onMarcar: (s: StageDef, p: MarcarPayload) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [data, setData] = useState("");
  const [link, setLink] = useState(pipeline?.reuniao_link ?? "");
  const [grupoId, setGrupoId] = useState(pipeline?.grupo_id ?? "");
  const [nota, setNota] = useState("");

  const temData = stage.extras?.includes("data");
  const temLink = stage.extras?.includes("link");
  const temGrupo = stage.extras?.includes("grupo_id");

  if (!aberto) {
    return (
      <button className="btn btn-primary" onClick={() => setAberto(true)}>
        Marcar concluído
      </button>
    );
  }

  return (
    <div className={styles.form}>
      {temData && (
        <label className="field">
          <span>Data</span>
          <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
        </label>
      )}
      {temGrupo && (
        <label className="field">
          <span>ID do grupo WhatsApp</span>
          <input value={grupoId} onChange={(e) => setGrupoId(e.target.value)} placeholder="grupo_id" />
        </label>
      )}
      {temLink && (
        <label className="field">
          <span>Link</span>
          <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" />
        </label>
      )}
      <label className="field">
        <span>Nota (opcional)</span>
        <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Observação desta etapa" />
      </label>
      <div className={styles.formAcoes}>
        <button
          className="btn btn-primary"
          disabled={salvando}
          onClick={() =>
            onMarcar(stage, {
              data: temData && data ? data : undefined,
              link: temLink ? link : undefined,
              grupo_id: temGrupo ? grupoId : undefined,
              nota: nota || undefined,
            })
          }
        >
          {salvando ? "Salvando…" : "Confirmar"}
        </button>
        <button className="btn btn-ghost" onClick={() => setAberto(false)} disabled={salvando}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Etapa 4 — Grupo WhatsApp: cola o link de convite, a Edge Function
// whatsapp-group-resolve puxa o metadado via Z-API (instância Yuri) e cadastra
// em `grupos`; depois marca a etapa concluída gravando o grupo_id resolvido.
// ----------------------------------------------------------------------------
function GrupoForm({
  stage,
  salvando,
  onMarcar,
}: {
  stage: StageDef;
  salvando: boolean;
  onMarcar: (s: StageDef, p: MarcarPayload) => void;
}) {
  const { show } = useToast();
  const [aberto, setAberto] = useState(false);
  const [link, setLink] = useState("");
  const [resolvendo, setResolvendo] = useState(false);
  const [grupo, setGrupo] = useState<Grupo | null>(null);
  const [nota, setNota] = useState("");

  async function buscar() {
    const l = link.trim();
    if (!l) return;
    setResolvendo(true);
    const r = await resolveWhatsappGroup(l);
    setResolvendo(false);
    if (r.ok && r.grupo) {
      setGrupo(r.grupo);
      show("success", `Grupo "${r.grupo.nome ?? r.grupo.grupo_id}" cadastrado.`);
    } else {
      show("error", r.message ?? "Não consegui ler o grupo por esse link.");
    }
  }

  function reset() {
    setAberto(false);
    setLink("");
    setGrupo(null);
    setNota("");
  }

  if (!aberto) {
    return (
      <button className="btn btn-primary" onClick={() => setAberto(true)}>
        Marcar concluído
      </button>
    );
  }

  return (
    <div className={styles.form}>
      <label className="field">
        <span>Link de convite do grupo</span>
        <input
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="https://chat.whatsapp.com/…"
          disabled={resolvendo || !!grupo}
        />
      </label>

      {!grupo && (
        <div className={styles.formAcoes}>
          <button className="btn btn-primary" onClick={buscar} disabled={resolvendo || !link.trim()}>
            {resolvendo ? "Buscando…" : "Buscar e cadastrar grupo"}
          </button>
          <button className="btn btn-ghost" onClick={reset} disabled={resolvendo}>
            Cancelar
          </button>
        </div>
      )}

      {grupo && (
        <>
          <div className={styles.grupoCard}>
            <div className={styles.grupoNome}>{grupo.nome ?? "(sem nome)"}</div>
            <div className="dim mono" style={{ fontSize: 12 }}>{grupo.grupo_id}</div>
          </div>
          <label className="field">
            <span>Nota (opcional)</span>
            <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Observação desta etapa" />
          </label>
          <div className={styles.formAcoes}>
            <button
              className="btn btn-primary"
              disabled={salvando}
              onClick={() => onMarcar(stage, { grupo_id: grupo.grupo_id, nota: nota || undefined })}
            >
              {salvando ? "Salvando…" : "Marcar concluído"}
            </button>
            <button className="btn btn-ghost" onClick={() => setGrupo(null)} disabled={salvando}>
              Trocar link
            </button>
          </div>
        </>
      )}
    </div>
  );
}
